import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Holding, Currency, LiquidityEvent, Settings, Snapshot, MacroShock } from '../types';
import { InvestmentWidget } from '../components/InvestmentWidget';
import { FireWidget } from '../components/FireWidget';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Input, Label, Select } from '../components/ui/Forms';
import { InfoTooltip } from '../components/ui/InfoTooltip';
import { runProjection, computeWeightedRates, buildIlliquidBuckets } from '../utils/projection';
import { convertCurrency, setOverrideRates } from '../utils/currency';
import { Settings2, Camera } from 'lucide-react';
import { AssetGrowthChart } from '../components/AssetGrowthChart';
interface ProjectionsPageProps {
  holdings: Holding[];
  baseCurrency: Currency;
  partnerNames: [string, string];
  totalLiquidInBase: number;
  totalIlliquidInBase: number;
  settings: Settings;
  onUpdateSettings: (settings: Settings) => void;
  liquidityEvents: LiquidityEvent[];
  snapshots?: Snapshot[];
  macroShocks?: MacroShock[];
}
export function ProjectionsPage({
  holdings,
  baseCurrency,
  partnerNames,
  totalLiquidInBase,
  totalIlliquidInBase,
  settings,
  onUpdateSettings,
  liquidityEvents,
  snapshots = [],
  macroShocks = []
}: ProjectionsPageProps) {
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>('live');
  const activeSnapshot = snapshots.find(s => s.id === selectedSnapshotId);
  // Apply snapshot exchange rates when viewing a snapshot, clear when returning to live
  useEffect(() => {
    if (activeSnapshot?.exchangeRates) {
      setOverrideRates(activeSnapshot.exchangeRates);
    } else {
      setOverrideRates(null);
    }
    return () => setOverrideRates(null); // Clean up on unmount
  }, [activeSnapshot]);
  // Use active snapshot data if selected, otherwise use live props
  const activeHoldings = activeSnapshot ? activeSnapshot.holdings : holdings;
  const activeSettings = activeSnapshot ? activeSnapshot.settings : settings;
  const activePartnerNames = activeSnapshot ? activeSnapshot.partnerNames : partnerNames;
  // If viewing a snapshot, we need to merge its liquidity events with its loans (if it has them)
  // If viewing live data, liquidityEvents already contains the merged loan events from App.tsx
  const activeLiquidityEvents = useMemo(() => {
    if (!activeSnapshot) return liquidityEvents;
    const snapshotEvents = activeSnapshot.liquidityEvents || [];
    // For backward compatibility with older snapshots that didn't have loans
    if (!activeSnapshot.loans || activeSnapshot.loans.length === 0) {
      return snapshotEvents;
    }
    // Simplified monthly payment calculation for snapshot display
    const loanEvents = activeSnapshot.loans.filter(loan => loan.isActive).map(loan => {
      const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
      const monthlyRate = loan.interestRate / 100 / 12;
      const n = loan.durationMonths;
      let monthlyPayment = 0;
      if (monthlyRate === 0) {
        monthlyPayment = principal / n;
      } else if (loan.repaymentType === 'annuity') {
        monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
      } else if (loan.repaymentType === 'interest-only') {
        monthlyPayment = principal * monthlyRate;
      } else if (loan.repaymentType === 'linear') {
        monthlyPayment = principal / n + principal * monthlyRate / 2;
      }
      const durationYears = Math.ceil(loan.durationMonths / 12);
      return {
        id: `loan-${loan.id}`,
        name: `${loan.name} Repayment`,
        amount: -monthlyPayment,
        currency: loan.currency,
        type: 'monthly' as const,
        startYear: loan.startYear,
        endYear: loan.startYear + durationYears - 1
      };
    });
    return [...snapshotEvents, ...loanEvents];
  }, [activeSnapshot, liquidityEvents]);
  const activeBaseCurrency = activeSnapshot ? activeSnapshot.settings.baseCurrency : baseCurrency;
  // Recompute totals if viewing a snapshot
  const activeTotalLiquid = activeSnapshot ? activeHoldings.filter(h => h.type === 'Liquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, activeBaseCurrency), 0) : totalLiquidInBase;
  const activeTotalIlliquid = activeSnapshot ? activeHoldings.filter(h => h.type === 'Illiquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, activeBaseCurrency), 0) : totalIlliquidInBase;
  // Compute weighted rates from holdings
  const {
    liquidRate,
    illiquidRate,
    weightedCGT,
    blendedRate
  } = useMemo(() => {
    return computeWeightedRates(activeHoldings, activeBaseCurrency);
  }, [activeHoldings, activeBaseCurrency]);
  // Build illiquid buckets for delayed drawdown availability
  const activeIlliquidBuckets = useMemo(() => {
    return buildIlliquidBuckets(activeHoldings, activeBaseCurrency);
  }, [activeHoldings, activeBaseCurrency]);
  const monthlyContribution = activeSettings.monthlyContribution ?? 0;
  const years = activeSettings.timeHorizon ?? 30;
  const updateSetting = <K extends keyof Settings,>(key: K, value: Settings[K]) => {
    if (activeSnapshot) return; // Disable updates when viewing a snapshot
    onUpdateSettings({
      ...settings,
      [key]: value
    });
  };
  // FIRE widget communicates its retirement year and drawdown back
  const [activeRetirementYear, setActiveRetirementYear] = useState<number | null>(null);
  const [activeDrawdown, setActiveDrawdown] = useState<number>(0);
  const handleRetirementYearChange = useCallback((year: number | null) => {
    setActiveRetirementYear(year);
  }, []);
  const handleDrawdownChange = useCallback((amount: number) => {
    setActiveDrawdown(amount);
  }, []);
  // Unified projection — one simulation, both widgets read from it
  const projectionData = useMemo(() => {
    let totalYears = years;
    if (activeRetirementYear !== null) {
      totalYears = Math.max(years, Math.min(activeRetirementYear + 10, years + 10));
    }
    return runProjection({
      totalLiquid: activeTotalLiquid,
      totalIlliquid: activeTotalIlliquid,
      liquidReturnRate: liquidRate,
      illiquidReturnRate: illiquidRate,
      drawdownReturnRate: blendedRate,
      monthlyContribution,
      retirementYear: activeRetirementYear !== null ? activeRetirementYear : years + 1,
      monthlyDrawdown: activeDrawdown,
      totalYears,
      events: activeLiquidityEvents,
      baseCurrency: activeBaseCurrency,
      annualInflation: activeSettings.inflationEnabled ? activeSettings.inflationRate ?? 3 : 0,
      capitalGainsTaxRate: weightedCGT,
      illiquidBuckets: activeIlliquidBuckets,
      macroShocks
    });
  }, [activeTotalLiquid, activeTotalIlliquid, liquidRate, illiquidRate, blendedRate, monthlyContribution, years, activeRetirementYear, activeDrawdown, activeLiquidityEvents, activeBaseCurrency, activeSettings, weightedCGT, activeIlliquidBuckets, macroShocks]);
  return <div className="space-y-8" data-id="element-1669">
      {/* Snapshot Selector */}
      {snapshots.length > 0 && <div className={`flex items-center justify-between gap-4 flex-wrap transition-colors`} data-id="element-1670">
          <div className="flex items-center gap-3" data-id="element-1671">
            <div className={`p-2 rounded-lg ${activeSnapshot ? 'bg-indigo-100' : 'bg-slate-100'}`} data-id="element-1672">
              <Camera className={`w-5 h-5 ${activeSnapshot ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-1673" />
            </div>
            <div data-id="element-1674">
              <h3 className={`text-sm font-semibold ${activeSnapshot ? 'text-indigo-900' : 'text-slate-600'}`} data-id="element-1675">
                {activeSnapshot ? 'Viewing Snapshot Data' : 'Viewing Live Data'}
              </h3>
              <p className={`text-xs ${activeSnapshot ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-1676">
                {activeSnapshot ? `Read-only view of data from ${new Date(activeSnapshot.createdAt).toLocaleString()}` : 'Currently viewing and editing your live portfolio'}
              </p>
            </div>
          </div>
          <div className="w-64 shrink-0" data-id="element-1677">
            <Select value={selectedSnapshotId} onChange={e => setSelectedSnapshotId(e.target.value)} options={[{
          value: 'live',
          label: '🟢 Live Data'
        }, ...snapshots.map(s => ({
          value: s.id,
          label: `📸 ${s.name} (${new Date(s.createdAt).toLocaleDateString()})`
        }))]} data-id="element-1678" />
          </div>
        </div>}

      {/* Computed Rates Info */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-id="element-1679">
        <Card className="bg-blue-50/50 border-blue-100" data-id="element-1680">
          <CardContent className="p-4" data-id="element-1681">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1" data-id="element-1682">
              Liquid Return
            </p>
            <p className="text-2xl font-bold text-blue-900" data-id="element-1683">
              {liquidRate.toFixed(1)}%
            </p>
            <p className="text-xs text-blue-700 mt-1" data-id="element-1684">
              Weighted avg of liquid holdings
            </p>
          </CardContent>
        </Card>
        <Card className="bg-indigo-50/50 border-indigo-100" data-id="element-1685">
          <CardContent className="p-4" data-id="element-1686">
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-1" data-id="element-1687">
              Illiquid Return
            </p>
            <p className="text-2xl font-bold text-indigo-900" data-id="element-1688">
              {illiquidRate.toFixed(1)}%
            </p>
            <p className="text-xs text-indigo-700 mt-1" data-id="element-1689">
              Weighted avg of illiquid & RE
            </p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200" data-id="element-1690">
          <CardContent className="p-4" data-id="element-1691">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1692">
              Blended Return
            </p>
            <p className="text-2xl font-bold text-slate-800" data-id="element-1693">
              {blendedRate.toFixed(1)}%
            </p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-1694">
              Overall portfolio growth rate
            </p>
          </CardContent>
        </Card>
        <Card className="bg-rose-50/50 border-rose-100" data-id="element-1695">
          <CardContent className="p-4" data-id="element-1696">
            <p className="text-xs font-semibold text-rose-600 uppercase tracking-wider mb-1" data-id="element-1697">
              Effective CGT
            </p>
            <p className="text-2xl font-bold text-rose-900" data-id="element-1698">
              {weightedCGT.toFixed(1)}%
            </p>
            <p className="text-xs text-rose-700 mt-1" data-id="element-1699">
              Weighted avg tax on returns
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Shared Planning Assumptions */}
      <section className={activeSnapshot ? 'opacity-90 pointer-events-none' : ''} data-id="element-1700">
        <Card data-id="element-1701">
          <CardHeader title="Planning Assumptions" subtitle={`Shared across Net Worth Projection & FIRE Planning below${activeSnapshot ? ' (Read-only in Snapshot)' : ''}`} icon={<div className="bg-blue-600 p-2 rounded-lg shadow-sm" data-id="element-1703">
                <Settings2 className="w-4 h-4 text-white" data-id="element-1704" />
              </div>} data-id="element-1702" />
          <CardContent className="space-y-6" data-id="element-1705">
            <div data-id="element-1706">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1707">
                Contributions & Timeline
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1708">
                <div data-id="element-1709">
                  <Label data-id="element-1710">
                    Monthly Contribution ({baseCurrency})
                    <InfoTooltip text="Amount invested each month during accumulation. Added to liquid holdings. Stops at retirement — the FIRE widget determines when that is." data-id="element-1711" />
                  </Label>
                  <Input type="number" min="0" step="100" value={monthlyContribution} onChange={e => updateSetting('monthlyContribution', Number(e.target.value))} data-id="element-1712" />
                </div>
                <div data-id="element-1713">
                  <Label data-id="element-1714">
                    Time Horizon
                    <InfoTooltip text="Total simulation window. The projection shows accumulation + drawdown within this period. FIRE planning searches for retirement within this window." data-id="element-1715" />
                  </Label>
                  <div className="flex items-center gap-3" data-id="element-1716">
                    <input type="range" min="1" max="60" step="1" value={years} onChange={e => updateSetting('timeHorizon', Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-1717" />
                    <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-sm w-14 text-center shrink-0" data-id="element-1718">
                      {years}y
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6" data-id="element-1719">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1720">
                Inflation Adjustment
              </p>
              <div className="flex items-start gap-4 max-w-2xl" data-id="element-1721">
                <label className="flex items-center gap-3 cursor-pointer" data-id="element-1722">
                  <input type="checkbox" checked={activeSettings.inflationEnabled ?? false} onChange={e => updateSetting('inflationEnabled', e.target.checked)} className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500" data-id="element-1723" />
                  <span className="text-sm text-slate-700" data-id="element-1724">
                    Increase drawdown annually to maintain purchasing power
                  </span>
                </label>
                {(activeSettings.inflationEnabled ?? false) && <div className="flex items-center gap-3 shrink-0" data-id="element-1725">
                    <input type="range" min="0.5" max="10" step="0.5" value={activeSettings.inflationRate ?? 3} onChange={e => updateSetting('inflationRate', Number(e.target.value))} className="w-32 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500" data-id="element-1726" />
                    <span className="font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded text-sm w-14 text-center shrink-0" data-id="element-1727">
                      {activeSettings.inflationRate ?? 3}%
                    </span>
                  </div>}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6" data-id="element-1728">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1729">
                FIRE Target
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl" data-id="element-1730">
                <div data-id="element-1731">
                  <Label data-id="element-1732">
                    Desired Monthly Income ({baseCurrency})
                    <InfoTooltip text="How much you want to withdraw each month in retirement. Used by both the projection chart and FIRE planning to determine sustainability." data-id="element-1733" />
                  </Label>
                  <Input type="number" min="0" step="100" value={activeSettings.desiredDrawdown ?? 5000} onChange={e => updateSetting('desiredDrawdown', Number(e.target.value))} data-id="element-1734" />
                  {(activeSettings.desiredDrawdown ?? 5000) > 0 && <p className="text-xs text-slate-400 mt-1.5" data-id="element-1735">
                      ={' '}
                      {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: activeBaseCurrency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format((activeSettings.desiredDrawdown ?? 5000) * 12)}
                      /year
                    </p>}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6" data-id="element-1736">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1737">
                Withdrawal Strategy
              </p>
              <div className="flex items-start gap-4 flex-wrap" data-id="element-1738">
                <button onClick={() => updateSetting('withdrawalStrategy', 'drawdown')} className={`flex-1 min-w-[200px] max-w-xs p-3 rounded-xl border-2 text-left transition-all ${activeSettings.withdrawalStrategy === 'drawdown' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1739">
                  <p className="text-sm font-semibold text-slate-800" data-id="element-1740">
                    Drawdown
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-1741">
                    Spend down principal over time horizon
                  </p>
                </button>
                <button onClick={() => updateSetting('withdrawalStrategy', 'interest-only')} className={`flex-1 min-w-[200px] max-w-xs p-3 rounded-xl border-2 text-left transition-all ${activeSettings.withdrawalStrategy === 'interest-only' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1742">
                  <p className="text-sm font-semibold text-slate-800" data-id="element-1743">
                    Interest Only
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-1744">
                    Live off returns, preserve principal forever
                  </p>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Additional components */}
      <section className={activeSnapshot ? 'opacity-90 pointer-events-none' : ''} data-id="element-1745">
        <InvestmentWidget baseCurrency={activeBaseCurrency} totalLiquid={activeTotalLiquid} totalIlliquid={activeTotalIlliquid} projectionData={projectionData} retirementYear={activeRetirementYear} partnerNames={activePartnerNames} partnerDOBs={activeSettings.partnerDOBs} liquidityEvents={activeLiquidityEvents} macroShocks={macroShocks} data-id="element-1746" />
      </section>

      <section className={activeSnapshot ? 'opacity-90 pointer-events-none' : ''} data-id="element-1747">
        <FireWidget baseCurrency={activeBaseCurrency} totalLiquid={activeTotalLiquid} totalIlliquid={activeTotalIlliquid} liquidReturnRate={liquidRate} illiquidReturnRate={illiquidRate} drawdownRate={blendedRate} monthlyContribution={monthlyContribution} timeHorizon={years} withdrawalStrategy={activeSettings.withdrawalStrategy} onRetirementYearChange={handleRetirementYearChange} onDrawdownChange={handleDrawdownChange} desiredDrawdown={activeSettings.desiredDrawdown ?? 5000} partnerNames={activePartnerNames} partnerDOBs={activeSettings.partnerDOBs} liquidityEvents={activeLiquidityEvents} annualInflation={activeSettings.inflationEnabled ? activeSettings.inflationRate ?? 3 : 0} capitalGainsTaxRate={weightedCGT} illiquidBuckets={activeIlliquidBuckets} macroShocks={macroShocks} data-id="element-1748" />
      </section>

      <section className={activeSnapshot ? 'opacity-90 pointer-events-none' : ''} data-id="element-1749">
        <AssetGrowthChart holdings={activeHoldings} baseCurrency={activeBaseCurrency} timeHorizon={years} macroShocks={macroShocks} data-id="element-1750" />
      </section>
    </div>;
}